import type { HTMLAttributes } from 'react'
import { graphHooks } from '@tunasong/graph-lib/react'
import { TemplateMenuItem } from '@tunasong/editor'
import { Copy, Delete, Move, Rename, RestoreFromTrash, RestorePage, Settings, Share, Transform } from '@tunasong/icons'
import { canWriteEntity, logger } from '@tunasong/models'
import {
  getActionsComponent,
  getPlugin,
  isStreamingCommand,
  useEntityCommandsBySource,
  useGlobalAppContext,
  usePlugins,
  type Command,
} from '@tunasong/plugin-lib'
import { features, useCurrentUser, useThunkDispatch } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'
import { isFolderContainer, isVersioned } from '@tunasong/schemas'
import {
  ConfirmDialog,
  Dialog,
  EntityCard,
  FavoriteMenuItem,
  getEntityPath,
  PinMenuItem,
  useAlert,
  useIsOwner,
  Button,
  MenuItem,
  ListItemIcon,
  ListItemText,
  DialogActions,
  DialogContent,
  TextField,
  Submenu,
  SubmenuContent,
  SubmenuTrigger,
} from '@tunasong/ui-lib'
import type { MouseEvent, ReactNode } from 'react'
import React, { useState } from 'react'
import invariant from 'tiny-invariant'
import { EntitySelectDialog } from '../entity/entity-select-dialog.js'
import { commandErrorMessage } from './command-error-message.js'
import { EntityCommandMenuItems } from './entity-command-menu-items.js'
import type { MenuGroupSpec } from './menu-groups.js'
import styles from './entity-menu.module.css'
import EntityHistory from '../entity/history.js'
import { useShare } from '../share/share.js'

export interface EntityMenuItemsProps extends HTMLAttributes<HTMLDivElement> {
  entity?: Persisted<Entity>
  /** Close the menu, but keep the dialogs open */
  onCloseMenu(): void
  /** Close everything */
  onClose(): void
}

/** The entity's own menu groups, shared by the More menu and the list/card item menu */
export interface EntityMenuGroups {
  /** Favorites, Pin, Use as template, Move…, Rename…, Duplicate */
  organise: ReactNode[]
  /** Share, Properties, Version history */
  share: ReactNode[]
  /** Add agent ›, and each existing agent's commands */
  agents: ReactNode[]
  /** Plugin conversions and actions */
  convert: ReactNode[]
  /** Move to trash, or Restore from trash */
  trash: ReactNode[]
}

/** Groups only the More menu has: the bar's folded controls, New…, and Video room */
export interface HostMenuGroups {
  view?: ReactNode[]
  create?: ReactNode[]
  collaborate?: ReactNode[]
}

/** More plugin commands than this collapse into one "Convert ›" submenu */
const maxFlatConvertCommands = 3

const emptyGroups: EntityMenuGroups = { organise: [], share: [], agents: [], convert: [], trash: [] }

/** The spec's group order: View, Create, Organise, Share and details, Agents, Convert, Collaborate, then Trash alone */
export const entityMenuGroups = (groups: EntityMenuGroups, host: HostMenuGroups = {}): MenuGroupSpec[] => [
  { key: 'view', items: host.view ?? [] },
  { key: 'create', items: host.create ?? [] },
  { key: 'organise', items: groups.organise },
  { key: 'share', items: groups.share },
  { key: 'agents', items: groups.agents },
  { key: 'convert', items: groups.convert },
  { key: 'collaborate', items: host.collaborate ?? [] },
  { key: 'trash', items: groups.trash },
]

/** @todo this is slowing down long lists */
export const useEntityMenuItems = ({ entity, onClose, onCloseMenu }: EntityMenuItemsProps) => {
  const dispatch = useThunkDispatch()
  const plugins = usePlugins('all')
  const { moveEntity } = graphHooks.useEntityUtil()
  const updateEntity = graphHooks.useEntityUpdate({ debounceDelay: 0 })
  const { cloneEntity } = graphHooks.useEntityClone()

  // get the entity commands — drop streaming commands here; they live in the
  // editor command palette where <StreamingResponse> drives the lifecycle.
  const commandsBySource = useEntityCommandsBySource(entity)
  const pluginCommands = commandsBySource.commands.filter((c): c is Command => !isStreamingCommand(c))
  const agentCommands = commandsBySource.agentCommands.filter((c): c is Command => !isStreamingCommand(c))
  const { dialog: shareDialog, show: showShare } = useShare(entity, onClose)

  const handleShare = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()
    showShare()
    onCloseMenu()
  }

  const isOwner = useIsOwner(entity)
  const { userId } = useCurrentUser()
  /** Moving needs write access to the item, and to where it goes (GH #2450) */
  const canMove = Boolean(entity && canWriteEntity(userId, entity))
  const moveDestinationReason = (folder: Persisted<Entity>) =>
    canWriteEntity(userId, folder) ? undefined : "You can't add items here"

  const [showMoveDialog, setShowMoveDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleShowMoveDialog = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowMoveDialog(true)
    onCloseMenu()
  }

  const handleCopy = (entity: Persisted<Entity>) => (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    void cloneEntity({ entity, updatedProps: { name: `Copy of ${entity.name}` } }).then(() => {
      onClose()
    })
  }

  const trash = graphHooks.useEntityTrash(entity)

  const handleTrash = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowDeleteDialog(false)
    trash()
    onClose()
  }

  const handleRestore = (entity: Persisted<Entity>) => (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    updateEntity(entity.id, { trash: false })
    onClose()
  }

  const handleShowDelete = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowDeleteDialog(true)
    onCloseMenu()
  }

  const handleHideDelete = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowDeleteDialog(false)
    onClose()
  }

  /** Rename */
  const [showRenameDialog, setShowRenameDialog] = useState(false)
  const handleShowRenameDialog = (open: boolean) => (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowRenameDialog(open)
    if (open) {
      onCloseMenu()
    } else {
      onClose()
    }
  }
  const [name, setName] = useState<string>()
  const handleRenameChange = (e: React.ChangeEvent<HTMLInputElement>) => setName(e.target.value)
  const handleRename = (entity: Persisted<Entity>) => (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    updateEntity(entity.id, { name })
    setShowRenameDialog(false)
    setName(undefined)
    onClose()
  }
  const [showHistory, setShowHistory] = useState(false)
  const handleShowHistory = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowHistory(true)
    onCloseMenu()
  }

  const handleHideHistory = () => {
    setShowHistory(false)
    onClose()
  }

  const [showProperties, setShowProperties] = useState(false)
  const handleShowProperties = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowDeleteDialog(false)
    setShowProperties(true)
    onCloseMenu()
  }
  const onCloseProperties = (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    setShowDeleteDialog(false)
    setShowProperties(false)
    onClose()
  }

  const handleProperties = (entity: Persisted<Entity>) => {
    updateEntity(entity.id, entity)
  }

  const handleMoveSelect = (entity: Persisted<Entity>) => (selected: Persisted<Entity> | undefined) => {
    setShowMoveDialog(false)
    onClose()

    if (!selected) {
      return
    }

    /** Move the entity */
    /** @todo handle ACLs */
    void moveEntity({ entity, newParent: selected })
    dispatch(
      features.notifications.actions.setAlert({
        title: 'Entity moved',
        severity: 'success',
        message: `${entity.name} moved to ${selected.name}`,
        link: getEntityPath({ entity: selected }),
      })
    )
  }

  const plugin = entity ? getPlugin(entity, plugins) : null

  const PluginBlockActions = getActionsComponent(plugins)

  const excludeIds = entity ? ([entity.id, entity.parentId].filter(Boolean) as string[]) : []

  const { prompt } = useGlobalAppContext()
  const { alert } = useAlert()

  const handleCommand = (command: Command) => (ev: MouseEvent) => {
    ev.preventDefault()
    ev.stopPropagation()

    const { cmd, cmdParamsSchema, cmdPrompt, name } = command
    invariant(cmd, 'No command found')

    const execute = async () => {
      const params = cmdParamsSchema
        ? await prompt({
            schema: cmdParamsSchema,
            title: cmdPrompt?.title ?? name,
            description: cmdPrompt?.description,
            okText: cmdPrompt?.confirmLabel,
            defaultValues: command.cmdDefaultValues,
          })
        : null

      onClose()

      // User cancelled the prompt dialog
      if (cmdParamsSchema && params === null) {
        return
      }

      try {
        await cmd(params)
      } catch (err) {
        logger.error('Error executing command', { name, err })
        alert({
          severity: 'error',
          entity,
          title: `Error executing command: ${name}`,
          message: commandErrorMessage(err),
          duration: 5000,
        })
      }
    }
    void execute()
  }

  return {
    dialogs: [
      shareDialog,
      ...(entity
        ? [
            <Dialog
              key={12}
              open={showProperties}
              title="Properties"
              onClose={onCloseProperties}
              onClick={ev => ev.stopPropagation()}
            >
              {plugin?.components?.Properties ? (
                <plugin.components.Properties element={entity} onChange={handleProperties} />
              ) : null}
            </Dialog>,
            entity ? (
              <Dialog
                key={13}
                open={showHistory}
                title="History"
                onClose={handleHideHistory}
                onClick={ev => ev.stopPropagation()}
              >
                <EntityHistory entity={entity} onComplete={handleHideHistory} />
              </Dialog>
            ) : null,
            entity ? (
              <EntitySelectDialog
                key={14}
                open={showMoveDialog}
                filter={isFolderContainer}
                title="Select folder"
                onClose={handleMoveSelect(entity)}
                excludeIds={excludeIds}
                disabledReason={moveDestinationReason}
              />
            ) : null,
            <ConfirmDialog
              key={15}
              open={showDeleteDialog}
              text={`Really move ${entity.type}: ${entity.name} to the trash?`}
              onCancel={handleHideDelete}
              onConfirm={handleTrash}
            >
              <EntityCard className={styles.entityCard} entity={entity} showMenu={false} headerAction={null} />
            </ConfirmDialog>,
            <Dialog
              key={16}
              open={showRenameDialog}
              title={`Rename ${entity.type}`}
              onClose={handleShowRenameDialog(false)}
            >
              <DialogContent>
                <TextField fullWidth autoFocus defaultValue={entity.name} onChange={handleRenameChange} />
              </DialogContent>
              <DialogActions>
                <Button onClick={handleRename(entity)} disabled={!name}>
                  Rename
                </Button>
              </DialogActions>
            </Dialog>,
          ]
        : []),
    ],
    groups: entity
      ? {
          organise: [
            <FavoriteMenuItem key="favorite" onClick={onClose} entity={entity} />,
            <PinMenuItem key="pin" onClick={onClose} entity={entity} />,
            plugin?.templatable ? <TemplateMenuItem key="template" element={entity} /> : null,
            <MenuItem key="move" onClick={handleShowMoveDialog} disabled={entity.trash || !canMove}>
              <ListItemIcon>
                <Move />
              </ListItemIcon>
              {/* Disabled with the reason, not hidden (GH #2450) */}
              <ListItemText primary="Move…" secondary={canMove ? undefined : "You can't move this item"} />
            </MenuItem>,
            <MenuItem key="rename" onClick={handleShowRenameDialog(true)} disabled={entity.trash}>
              <ListItemIcon>
                <Rename />
              </ListItemIcon>
              <ListItemText primary="Rename…" />
            </MenuItem>,
            <MenuItem key="duplicate" onClick={handleCopy(entity)} disabled={entity.trash}>
              <ListItemIcon>
                <Copy />
              </ListItemIcon>
              <ListItemText primary="Duplicate" />
            </MenuItem>,
          ],
          share: [
            <MenuItem key="share" onClick={handleShare} disabled={entity.trash}>
              <ListItemIcon>
                <Share />
              </ListItemIcon>
              <ListItemText primary="Share" />
            </MenuItem>,
            plugin?.components?.Properties ? (
              <MenuItem key="properties" onClick={handleShowProperties}>
                <ListItemIcon>
                  <Settings />
                </ListItemIcon>
                <ListItemText primary="Properties" />
              </MenuItem>
            ) : null,
            isVersioned(entity) ? (
              <MenuItem key="history" onClick={handleShowHistory} disabled={!isOwner}>
                <ListItemIcon>
                  <RestorePage />
                </ListItemIcon>
                <ListItemText primary="Version history" />
              </MenuItem>
            ) : null,
          ],
          // Every "Add agent: X" shares one `groupId`, so <EntityCommandMenuItems> collapses them into "Add agent ›";
          // an existing agent's commands keep their own flyout (#1483).
          agents:
            agentCommands.length > 0
              ? [<EntityCommandMenuItems key="agents" commands={agentCommands} getHandler={handleCommand} />]
              : [],
          convert: [
            /** @deprecated - move to entity commands instead */
            <PluginBlockActions key="plugin-actions" onClose={onClose} element={entity} />,
            ...(pluginCommands.length > maxFlatConvertCommands
              ? [
                  <Submenu key="convert">
                    <SubmenuTrigger>
                      <ListItemIcon>
                        <Transform />
                      </ListItemIcon>
                      <ListItemText primary="Convert" />
                    </SubmenuTrigger>
                    <SubmenuContent aria-label="Convert">
                      <EntityCommandMenuItems commands={pluginCommands} getHandler={handleCommand} />
                    </SubmenuContent>
                  </Submenu>,
                ]
              : pluginCommands.length > 0
                ? [<EntityCommandMenuItems key="commands" commands={pluginCommands} getHandler={handleCommand} />]
                : []),
          ],
          trash: [
            entity.trash ? (
              <MenuItem key="restore" onClick={handleRestore(entity)} disabled={!isOwner}>
                <ListItemIcon>
                  <RestoreFromTrash />
                </ListItemIcon>
                <ListItemText primary="Restore from trash" />
              </MenuItem>
            ) : (
              <MenuItem key="trash" onClick={handleShowDelete} disabled={!isOwner}>
                <ListItemIcon>
                  <Delete color="var(--tuna-palette-error-main)" />
                </ListItemIcon>
                <ListItemText primary="Move to trash" />
              </MenuItem>
            ),
          ],
        }
      : emptyGroups,
  }
}
